/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const materials = [
  { name: "Lumber / Framing", waste: 7.5 },
  { name: "Drywall", waste: 11 },
  { name: "Roofing Shingles", waste: 12.5 },
  { name: "Concrete", waste: 6.5 },
  { name: "Tile (floor)", waste: 12.5 },
  { name: "Carpet", waste: 10 },
  { name: "Paint", waste: 12.5 },
  { name: "Insulation", waste: 6.5 },
  { name: "Siding (vinyl)", waste: 11 },
  { name: "Ductwork", waste: 10 },
  { name: "Custom", waste: 10 },
];

export function WasteCalculatorClient() {
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("sq ft");
  const [materialIdx, setMaterialIdx] = useState(0);
  const [customWaste, setCustomWaste] = useState("");
  const [costPerUnit, setCostPerUnit] = useState("");

  const selected = materials[materialIdx];
  if (!selected) return null;
  const wastePercent =
    selected.name === "Custom"
      ? parseFloat(customWaste) || 0
      : selected.waste;
  const qty = parseFloat(quantity) || 0;
  const cpu = costPerUnit;
  const cost = parseFloat(cpu) || 0;

  const wasteAmount = qty * (wastePercent / 100);
  const totalNeeded = qty + wasteAmount;
  const hasResult = qty > 0;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const fmtCost = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-foreground">Material</label>
          <select
            value={materialIdx}
            onChange={(e) => setMaterialIdx(Number(e.target.value))}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            {materials.map((m, i) => (
              <option key={m.name} value={i}>
                {m.name} ({m.name === "Custom" ? "set your own" : `${m.waste}%`})
              </option>
            ))}
          </select>
        </div>
        {selected.name === "Custom" && (
          <div>
            <label className="block text-sm font-medium text-foreground">Waste %</label>
            <input
              type="number"
              value={customWaste}
              onChange={(e) => setCustomWaste(e.target.value)}
              placeholder="10"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground">Net Quantity Needed</label>
          <div className="mt-1 flex gap-2">
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="1000"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-foreground"
            >
              <option>sq ft</option>
              <option>linear ft</option>
              <option>units</option>
              <option>sheets</option>
              <option>cubic yards</option>
              <option>gallons</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Cost Per Unit (optional)</label>
          <input
            type="number"
            value={costPerUnit}
            onChange={(e) => setCostPerUnit(e.target.value)}
            placeholder="2.50"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Net Quantity</div>
              <div className="text-2xl font-bold text-foreground">{fmt(qty)} {unit}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Waste ({wastePercent}%)</div>
              <div className="text-2xl font-bold text-secondary">{fmt(wasteAmount)} {unit}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Order Quantity</div>
              <div className="text-2xl font-bold text-secondary">{fmt(totalNeeded)} {unit}</div>
            </div>
            {cost > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">Total Cost</div>
                <div className="text-2xl font-bold text-foreground">${fmtCost(totalNeeded * cost)}</div>
              </div>
            )}
          </div>
          <ResultActions
            toolSlug="waste-calculator"
            inputs={{ quantity, unit, materialIdx, customWaste, costPerUnit }}
            outputs={{ wastePercent, wasteAmount, totalNeeded }}
          />
        </>
      )}
    </div>
  );
}
